import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getBackendUrl } from '../lib/utils';

// API Base URL - in a real app this might be from env
const API_URL = `${getBackendUrl()}/api/admin/review`;

const getAuthHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${data.session?.access_token}`
  };
};

export const AdminReviewPage = () => {
  const [activeQueue, setActiveQueue] = useState<'skill_nodes' | 'pedagogy_cards'>('skill_nodes');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [currentItem, setCurrentItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/counts`, { headers });
      if (!res.ok) throw new Error('Failed to fetch counts');
      const data = await res.json();
      setCounts(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fetchNextItem = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/pending?type=${activeQueue}`, { headers });
      
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
           throw new Error('Unauthorized or Forbidden: You do not have admin access.');
        }
        throw new Error('Failed to fetch item');
      }
      
      const { data } = await res.json();
      setCurrentItem(data || null);
    } catch (err: any) {
      setError(err.message);
      setCurrentItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
    fetchNextItem();
  }, [activeQueue]);

  return (
    <div className="flex h-screen bg-neutral-900 text-white">
      <ReviewQueueSidebar 
        counts={counts} 
        activeQueue={activeQueue} 
        setActiveQueue={setActiveQueue} 
      />
      
      <main className="flex-1 overflow-auto p-8">
        {loading ? (
          <div className="flex items-center justify-center h-full">Loading...</div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-500">{error}</div>
        ) : currentItem ? (
          <ReviewItemDetail 
            item={currentItem} 
            type={activeQueue} 
            onNext={() => { fetchCounts(); fetchNextItem(); }} 
          />
        ) : (
          <div className="flex items-center justify-center h-full text-neutral-400">
            Queue is empty. Great job!
          </div>
        )}
      </main>
    </div>
  );
};

const ReviewQueueSidebar = ({ counts, activeQueue, setActiveQueue }: any) => {
  return (
    <aside className="w-64 bg-neutral-950 border-r border-neutral-800 p-4">
      <h2 className="text-xl font-bold mb-6 text-neutral-200">Review Queue</h2>
      <nav className="space-y-2">
        {(['skill_nodes', 'pedagogy_cards'] as const).map(queue => (
          <button
            key={queue}
            onClick={() => setActiveQueue(queue)}
            className={`w-full text-left px-4 py-3 rounded-lg flex justify-between items-center transition-colors ${
              activeQueue === queue ? 'bg-indigo-600 text-white' : 'hover:bg-neutral-800 text-neutral-400'
            }`}
          >
            <span className="capitalize">{queue.replace('_', ' ')}</span>
            <span className="bg-neutral-900 px-2 py-1 rounded text-xs">
              {counts[queue] || 0}
            </span>
          </button>
        ))}
      </nav>
    </aside>
  );
};

const ReviewItemDetail = ({ item, type, onNext }: any) => {
  const [editedItem, setEditedItem] = useState(item);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync state if item changes (e.g., fetching next)
  useEffect(() => { setEditedItem(item); setNote(''); }, [item]);

  const handleAction = async (action: 'approve' | 'reject' | 'edit', withEdit = false) => {
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      
      if (action === 'edit' || withEdit) {
        // Save edits first
        const patchRes = await fetch(`${API_URL}/${type}/${item.id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(editedItem) // Send the entire edited item, backend Zod handles pruning
        });
        if (!patchRes.ok) throw new Error('Failed to save edits');
      }

      if (action !== 'edit') {
        const url = `${API_URL}/${type}/${item.id}/${action}`;
        const body = action === 'reject' ? JSON.stringify({ review_note: note }) : undefined;
        
        const res = await fetch(url, { method: 'POST', headers, body });
        if (!res.ok) {
           const errData = await res.json();
           throw new Error(errData.error || `Failed to ${action}`);
        }
        
        onNext(); // Move to next item
      } else {
        // Just saved edits, remain on page
        alert('Edits saved successfully.');
      }
    } catch (err: any) {
      alert(`Action failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Reviewing {type === 'skill_nodes' ? 'Skill Node' : 'Pedagogy Card'}</h1>
        <ReviewStatusBadge status={item.review_status} />
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-xl p-6">
          <EditableFieldsForm 
            item={editedItem} 
            type={type} 
            onChange={(field: string, val: any) => setEditedItem({ ...editedItem, [field]: val })} 
          />
        </div>
        <div className="col-span-1 space-y-6">
          <ProvenancePanel item={item} />
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
            <h3 className="font-semibold text-neutral-300 mb-2">Rejection Note</h3>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-700 rounded p-2 text-sm text-neutral-200 focus:outline-none focus:border-indigo-500"
              placeholder="Reason for rejection (required to reject)..."
              aria-label="Reason for rejection"
              rows={3}
            />
          </div>
        </div>
      </div>

      <ReviewActionsBar 
        onApprove={() => handleAction('approve')} 
        onReject={() => handleAction('reject')} 
        onSave={() => handleAction('edit')}
        onSaveAndApprove={() => handleAction('approve', true)}
        onSkip={onNext}
        disabled={submitting}
        canReject={note.trim().length > 0}
      />
    </div>
  );
};

const EditableFieldsForm = ({ item, type, onChange }: any) => {
  const fields = type === 'skill_nodes' 
    ? ['name', 'title', 'description', 'cefr_level', 'skill_type', 'difficulty', 'priority_weight', 'teaching_method']
    : ['title', 'card_type', 'content', 'cefr_level', 'skill_id'];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold border-b border-neutral-800 pb-2">Content Data</h3>
      {fields.map(field => {
        if (item[field] === undefined) return null;
        
        const isObject = typeof item[field] === 'object';
        
        return (
          <div key={field} className="space-y-1">
            <label className="text-xs text-neutral-400 font-medium uppercase tracking-wider">{field.replace('_', ' ')}</label>
            {isObject ? (
              <textarea
                value={JSON.stringify(item[field], null, 2)}
                onChange={(e) => {
                   try { onChange(field, JSON.parse(e.target.value)); } catch(e) {}
                }}
                className="w-full bg-neutral-950 border border-neutral-700 rounded p-3 font-mono text-sm min-h-[150px]"
              />
            ) : (
              <input
                type={typeof item[field] === 'number' ? 'number' : 'text'}
                value={item[field] || ''}
                onChange={(e) => {
                  const val = typeof item[field] === 'number' ? Number(e.target.value) : e.target.value;
                  onChange(field, val);
                }}
                className="w-full bg-neutral-950 border border-neutral-700 rounded p-3 text-sm"
              />
            )}
          </div>
        )
      })}
    </div>
  );
};

const ProvenancePanel = ({ item }: any) => {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 text-sm">
      <h3 className="font-semibold text-neutral-300 mb-4 border-b border-neutral-800 pb-2">Provenance</h3>
      <dl className="space-y-3">
        <div>
          <dt className="text-neutral-500 text-xs uppercase tracking-wider">Parser Version</dt>
          <dd className="text-neutral-300 font-mono truncate">{item.parser_version || 'N/A'}</dd>
        </div>
        <div>
          <dt className="text-neutral-500 text-xs uppercase tracking-wider">Source Section</dt>
          <dd className="text-neutral-300 font-mono truncate">{item.source_section || 'N/A'}</dd>
        </div>
        <div>
          <dt className="text-neutral-500 text-xs uppercase tracking-wider">Excerpt Hash</dt>
          <dd className="text-neutral-300 font-mono truncate" title={item.source_excerpt_hash}>{item.source_excerpt_hash || 'N/A'}</dd>
        </div>
        <div>
          <dt className="text-neutral-500 text-xs uppercase tracking-wider">Prompt Hash</dt>
          <dd className="text-neutral-300 font-mono truncate" title={item.extraction_prompt_hash}>{item.extraction_prompt_hash || 'N/A'}</dd>
        </div>
        <div>
          <dt className="text-neutral-500 text-xs uppercase tracking-wider">Extracted Date</dt>
          <dd className="text-neutral-300">{new Date(item.created_at).toLocaleString()}</dd>
        </div>
      </dl>
    </div>
  );
};

const ReviewActionsBar = ({ onApprove, onReject, onSave, onSaveAndApprove, onSkip, disabled, canReject }: any) => {
  return (
    <div className="bg-neutral-950 border-t border-neutral-800 p-4 rounded-xl flex items-center justify-between sticky bottom-4 shadow-2xl">
      <div className="flex gap-2">
        <button onClick={onSkip} disabled={disabled} className="px-4 py-2 rounded font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 transition">
          Skip for now
        </button>
      </div>
      <div className="flex gap-3">
        <button onClick={onSave} disabled={disabled} className="px-4 py-2 rounded font-medium border border-neutral-700 text-neutral-300 hover:bg-neutral-800 transition">
          Save Edits
        </button>
        <button onClick={onReject} disabled={disabled || !canReject} className={`px-4 py-2 rounded font-medium border border-red-900/50 text-red-400 transition ${canReject ? 'hover:bg-red-950' : 'opacity-50 cursor-not-allowed'}`}>
          Reject
        </button>
        <button onClick={onSaveAndApprove} disabled={disabled} className="px-4 py-2 rounded font-medium border border-indigo-700 text-indigo-300 hover:bg-indigo-950 transition">
          Save & Approve
        </button>
        <button onClick={onApprove} disabled={disabled} className="px-6 py-2 rounded font-bold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow-lg shadow-indigo-900/20">
          Approve
        </button>
      </div>
    </div>
  );
};

const ReviewStatusBadge = ({ status }: { status: string }) => {
  return (
    <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-900/50 text-amber-400 border border-amber-800/50">
      {status.replace('_', ' ')}
    </span>
  );
};

export default AdminReviewPage;
